import React, { useState, useEffect } from 'react';
import { Users, Calendar, Package, CheckCircle, Clock, TrendingUp, Truck, MessageSquare } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { fatouReservationService } from '../../services/fatouReservationService';
import ReservationCaptureTable from './components/ReservationCaptureTable';
import DataExtractionPanel from './components/DataExtractionPanel';
import ValidationStatusPanel from './components/ValidationStatusPanel';
import ReservationDetailModal from './components/ReservationDetailModal';
import { FatouConversationsContent } from '../admin-fatou-conversations';

export default function FatouAIReservationCaptureSystem() {
  const [activeTab, setActiveTab] = useState('conversations');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    searchQuery: '',
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    loadDashboardData();
    loadReservations();

    const channel = fatouReservationService?.subscribeToReservations((payload) => {
      if (payload?.eventType === 'INSERT') {
        setReservations(prev => [payload?.new, ...prev]);
        loadDashboardData();
      } else if (payload?.eventType === 'UPDATE') {
        setReservations(prev => prev?.map(r => r?.id === payload?.new?.id ? payload?.new : r));
        if (selectedReservation?.id === payload?.new?.id) {
          setSelectedReservation(payload?.new);
        }
        loadDashboardData();
      } else if (payload?.eventType === 'DELETE') {
        setReservations(prev => prev?.filter(r => r?.id !== payload?.old?.id));
        loadDashboardData();
      }
    });

    return () => { fatouReservationService?.unsubscribe(channel); };
  }, []);

  useEffect(() => {
    loadReservations();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      const statsData = await fatouReservationService?.getReservationStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const loadReservations = async () => {
    try {
      setLoading(true);
      const data = await fatouReservationService?.getReservations({ ...filters, limit: 100 });
      setReservations(data);
    } catch (error) {
      console.error('Error loading reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status, notes) => {
    try {
      await fatouReservationService?.updateReservationStatus(id, status, notes);
      await loadReservations();
      await loadDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUpdateReservation = async (id, updates) => {
    try {
      await fatouReservationService?.updateReservation(id, updates);
      await loadReservations();
      setSelectedReservation(null);
    } catch (error) {
      console.error('Error updating reservation:', error);
      alert('Erreur lors de la mise à jour de la réservation');
    }
  };

  const handleDeleteReservation = async (id) => {
    try {
      await fatouReservationService?.deleteReservation(id);
      setReservations(prev => prev?.filter(r => r?.id !== id));
      await loadDashboardData();
      showSuccess('Réservation supprimée avec succès.');
    } catch (error) {
      console.error('Error deleting reservation:', error);
      throw error;
    }
  };

  const handleModalUpdate = (updatedReservation) => {
    setReservations(prev => prev?.map(r => r?.id === updatedReservation?.id ? updatedReservation : r));
    setSelectedReservation(updatedReservation);
    loadDashboardData();
  };

  const handleModalDelete = (id) => {
    setReservations(prev => prev?.filter(r => r?.id !== id));
    setSelectedReservation(null);
    loadDashboardData();
    showSuccess('Réservation supprimée avec succès.');
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const tabs = [
    { id: 'conversations', label: 'Conversations', icon: MessageSquare },
    { id: 'capture', label: 'Réservations', icon: TrendingUp, count: stats?.totalReservations },
    { id: 'extraction', label: 'Extraction', icon: Package },
    { id: 'validation', label: 'Validation', icon: CheckCircle, count: stats?.pendingCount }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-green-100 rounded-xl">
              <Package className="text-green-600" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Espace Fatou IA</h1>
              <p className="text-sm text-gray-500">Conversations, réservations et suivi des commandes Fatou IA</p>
            </div>
          </div>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="mb-4 flex items-center gap-2 bg-green-100 text-green-700 border border-green-200 rounded-xl px-4 py-3 text-sm font-medium">
            <CheckCircle size={16} />
            {successMessage}
          </div>
        )}

        {/* Stats Cards — only shown when not on Conversations tab */}
        {activeTab !== 'conversations' && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.totalReservations || 0}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg"><Users className="text-blue-600" size={20} /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.todayCount || 0}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg"><Calendar className="text-green-600" size={20} /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">En Attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.pendingCount || 0}</p>
                </div>
                <div className="p-2 bg-yellow-100 rounded-lg"><Clock className="text-yellow-600" size={20} /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Affectées</p>
                  <p className="text-2xl font-bold text-purple-600">{stats?.assignedCount || 0}</p>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg"><Truck className="text-purple-600" size={20} /></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Confirmées</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.confirmedCount || 0}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="text-green-600" size={20} /></div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${activeTab !== 'conversations' ? 'mb-6' : ''}`}>
          <div className="border-b border-gray-100">
            <div className="flex space-x-1 p-2 overflow-x-auto">
              {tabs?.map((tab) => {
                const TabIcon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
                      activeTab === tab?.id ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <TabIcon size={16} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === tab?.id ? 'bg-white text-green-600' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'conversations' ? (
            <FatouConversationsContent containerHeight="calc(100vh - 220px)" />
          ) : (
            <div className="p-6">
              {loading && activeTab === 'capture' ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-3"></div>
                    <p className="text-gray-500 text-sm">Chargement des réservations...</p>
                  </div>
                </div>
              ) : (
                <>
                  {activeTab === 'capture' && (
                    <ReservationCaptureTable
                      reservations={reservations}
                      filters={filters}
                      onFiltersChange={setFilters}
                      onUpdateStatus={handleUpdateStatus}
                      onSelectReservation={setSelectedReservation}
                      onRefresh={loadReservations}
                      onDelete={handleDeleteReservation}
                    />
                  )}
                  {activeTab === 'extraction' && (
                    <DataExtractionPanel
                      reservations={reservations}
                      selectedReservation={selectedReservation}
                      onSelectReservation={setSelectedReservation}
                      onUpdateReservation={handleUpdateReservation}
                    />
                  )}
                  {activeTab === 'validation' && (
                    <ValidationStatusPanel
                      reservations={reservations?.filter(r => r?.status === 'pending')}
                      onUpdateStatus={handleUpdateStatus}
                      onRefresh={loadReservations}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedReservation && (
        <ReservationDetailModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onUpdate={handleModalUpdate}
          onDelete={handleModalDelete}
        />
      )}
    </div>
  );
}